import { z } from 'zod';
import { horaDe, horaNoRelogio, MINUTOS_DO_DIA, minutosDe } from '../tempo/index.js';
/**
 * O catálogo de configurações da arena.
 *
 * Chave/valor TIPADO, não JSON solto: cada chave declara o seu schema
 * zod e o seu padrão, e a leitura valida antes de entregar. JSON livre
 * vira campo que ninguém sabe o formato em três meses.
 *
 * Mora em @arena/shared porque a tela de configurações do frontend
 * valida o mesmo formulário com o mesmo schema — a regra do projeto é
 * que o schema é a fonte e o tipo TypeScript é inferido dele.
 *
 * Ver: Arena_backend/Docs/plano/02-identidade-sessao.md
 */
/** "07:00", "21:30". Minuto a minuto, sem fuso — o fuso é da arena. */
export const zHoraMinuto = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use o formato HH:MM, de 00:00 a 23:59.');
/**
 * A meia-noite do FIM do dia, em forma canônica.
 *
 * A arena que fecha à meia-noite não conseguia gravar o horário, e o
 * motivo é de representação: `<input type="time">` não sabe dizer
 * "24:00" — ele manda "00:00", que como TEXTO vem antes de qualquer
 * abertura. A janela "07:00 às 00:00" era recusada por "a abertura
 * precisa ser anterior ao fechamento", e o horário simplesmente não
 * mudava.
 *
 * "24:00" resolve porque o resto do sistema já o entende, sem nenhuma
 * conta nova:
 *
 *   `minutosDe('24:00')`              1440 — e `horaDe` sempre
 *                                     documentou 1440 como a meia-noite
 *                                     do fim do dia
 *   `instanteLocal(dia, '24:00')`     exatamente a meia-noite do dia
 *                                     SEGUINTE, inclusive na virada do
 *                                     horário de verão
 *   `'07:00' < '24:00'`               verdadeiro, então a comparação de
 *                                     texto volta a valer
 *
 * A grade, o motor de conflito, as janelas livres e o horário oferecível
 * continuam como estavam: todos leem minutos ou instantes.
 *
 * A arena que VIRA O DIA — abre às 08:00 e fecha às 02:00 da manhã
 * seguinte — é a mesma ideia levada adiante: o fechamento vira "26:00".
 * Ver `janelaCanonica` e o dia da grade em `@arena/shared/tempo`.
 */
export const MEIA_NOITE_DO_FIM_DO_DIA = '24:00';
/**
 * A hora de FECHAR: "HH:MM" comum, ou a meia-noite do fim do dia.
 *
 * Aceita o "00:00" que o navegador manda e o canoniza em "24:00" — a
 * tradução acontece aqui, na borda, e não espalhada por quem lê a
 * configuração.
 */
export const zHoraDeFechamento = z
    .string()
    .regex(/^(([01]\d|2[0-3]):[0-5]\d|24:00)$/, 'Use o formato HH:MM, de 00:00 a 23:59 — ou 00:00 para fechar à meia-noite.')
    .transform((hora) => (hora === '00:00' ? MEIA_NOITE_DO_FIM_DO_DIA : hora));
/**
 * "24:00" é meia-noite para quem lê, e "26:00" são as 02:00. O canônico
 * serve ao cálculo, não à pessoa.
 */
export function fechamentoLegivel(fim) {
    return horaNoRelogio(fim);
}
/** A janela fecha DEPOIS da meia-noite — na madrugada do dia seguinte? */
export function fechaNoDiaSeguinte(janela) {
    return minutosDe(janela.fim) > MINUTOS_DO_DIA;
}
/** "08:00 às 02:00 do dia seguinte", "07:00 às 00:00", "07:00 às 22:00". */
export function horarioLegivel(janela) {
    const fim = fechamentoLegivel(janela.fim);
    return `${janela.inicio} às ${fim}${fechaNoDiaSeguinte(janela) ? ' do dia seguinte' : ''}`;
}
/**
 * A janela na forma canônica: fechamento que não vem DEPOIS da abertura
 * é a madrugada seguinte.
 *
 *   08:00 → 22:00    fica como está
 *   07:00 → 00:00    "24:00", a meia-noite do fim do dia
 *   08:00 → 02:00    "26:00", as 02:00 da manhã seguinte
 *   00:00 → 00:00    "24:00", o dia inteiro
 *
 * O `<input type="time">` só sabe dizer "02:00", e quem está na tela
 * escolhe "Fecha 02:00" pensando na noite, não num horário que vem antes
 * de abrir. Não há leitura que faça sentido para "fecha antes de abrir no
 * mesmo dia" — por isso a regra não pergunta, traduz.
 *
 * Pura e sem validar, para a tela de configurações mostrar o efeito
 * enquanto a pessoa digita; o schema abaixo é quem recusa.
 */
export function janelaCanonica(janela) {
    const abre = minutosDe(janela.inicio);
    let fecha = minutosDe(janela.fim);
    if (fecha <= abre)
        fecha += MINUTOS_DO_DIA;
    return { inicio: janela.inicio, fim: horaDe(fecha) };
}
/**
 * O fechamento como chega à janela: "HH:MM" do relógio, ou já canônico —
 * "24:00" a "47:59", que é como ele volta do banco e passa de novo por
 * aqui na leitura.
 */
const zFechamentoDaJanela = z
    .string()
    .regex(/^([0-3]\d|4[0-7]):[0-5]\d$/, 'Use o formato HH:MM, de 00:00 a 23:59.');
export const zJanelaHoraria = z
    .object({ inicio: zHoraMinuto, fim: zFechamentoDaJanela })
    .transform(janelaCanonica)
    .refine((j) => minutosDe(j.fim) - minutosDe(j.inicio) <= MINUTOS_DO_DIA, {
    message: 'A arena fica aberta no máximo 24 horas seguidas.',
    path: ['fim'],
});
/**
 * 0 = domingo … 6 = sábado, o mesmo `getDay()` do JavaScript.
 *
 * Esta é a pendência P3, e ela não é detalhe cosmético: é o
 * DENOMINADOR do indicador de ocupação do painel. Trocar a faixa muda
 * o número que aparece na tela — por isso é configuração por arena, e
 * não constante no código.
 */
export const zFaixaHorarioNobre = z
    .object({
    inicio: zHoraMinuto,
    // Como o funcionamento: a faixa nobre pode ir até a meia-noite.
    fim: zHoraDeFechamento,
    dias: z.array(z.number().int().min(0).max(6)).min(1, 'Escolha ao menos um dia.'),
})
    .refine((f) => f.inicio < f.fim, {
    message: 'O início da faixa precisa ser anterior ao fim.',
    path: ['fim'],
});
/** Percentual em pontos, não fração: 2 significa 2%. */
export const zPercentual = z.number().min(0).max(100);
/**
 * Expiração de sessão POR PERFIL — o card Segurança de #v-acessos
 * promete isso. A chave é a chave do perfil ('dono', 'gestao', …), o
 * valor é em horas.
 */
/**
 * A duração de quem não tem linha na configuração — um perfil criado
 * depois da arena, por exemplo. O login e o card Segurança leem daqui,
 * para que a tela nunca mostre um número e o login aplique outro.
 */
export const HORAS_DE_SESSAO_PADRAO = 8;
export const zDuracaoSessaoHoras = z.record(z.string(), z
    .number()
    .int()
    .positive()
    .max(24 * 30));
export const zVersaoTermo = z.string().regex(/^\d+\.\d+$/, 'Use o formato "1.2".');
/** Nome IANA. Validado de verdade no backend, que tem Intl completo. */
export const zFuso = z.string().min(3);
/**
 * 12 · planos — a cadência da cobrança DEPOIS do vencimento: em quantos
 * dias depois dele a régua publica `fatura.vencida`.
 *
 * O documento 12 deixa isso em aberto ("uma vez, ou a cada N dias, e até
 * quando") e diz que precisa do dono. Configuração por arena é o jeito de
 * a resposta dele não virar deploy: o padrão, [1, 7, 15], é provisório e
 * está no relatório da fase. Repetidos saem; a ordem é crescente.
 */
export const zDiasDaReguaDepoisDoVencimento = z
    .array(z
    .number({ invalid_type_error: 'Use números de dias.' })
    .int('Dias inteiros.')
    .min(1, 'O primeiro aviso sai no mínimo um dia depois do vencimento.')
    .max(90, 'No máximo 90 dias depois do vencimento.'))
    .min(1, 'Informe ao menos um dia.')
    .max(6, 'No máximo seis avisos depois do vencimento.')
    .transform((dias) => [...new Set(dias)].sort((a, b) => a - b));
export const CONFIGURACOES = {
    horario_funcionamento: {
        rotulo: 'Horário de funcionamento',
        descricao: 'Limita a grade da agenda e o formulário público de reservas.',
        schema: zJanelaHoraria,
        padrao: { inicio: '07:00', fim: '22:00' },
    },
    faixa_horario_nobre: {
        rotulo: 'Faixa de horário nobre',
        descricao: 'Denominador do indicador de ocupação. Padrão assumido pela pendência P3, ' +
            'ainda pendente de confirmação do dono.',
        schema: zFaixaHorarioNobre,
        padrao: { inicio: '17:00', fim: '21:00', dias: [1, 2, 3, 4, 5] },
    },
    multa_percentual: {
        rotulo: 'Multa por atraso (%)',
        descricao: 'Aplicada uma vez sobre a fatura vencida.',
        schema: zPercentual,
        padrao: 2,
    },
    juros_mes_percentual: {
        rotulo: 'Juros ao mês (%)',
        descricao: 'Proporcional aos dias de atraso.',
        schema: zPercentual,
        padrao: 1,
    },
    duracao_sessao_horas: {
        rotulo: 'Duração da sessão por perfil (horas)',
        descricao: 'Quanto tempo o refresh token sobrevive sem novo login.',
        schema: zDuracaoSessaoHoras,
        padrao: { dono: 8, gestao: 8, professor: 8, aluno: 8 },
    },
    versao_termo_vigente: {
        rotulo: 'Versão do termo vigente',
        descricao: 'Gravada em cada aceite; o protótipo exibe "versão 1.2" na ficha do aluno.',
        schema: zVersaoTermo,
        padrao: '1.2',
    },
    fuso: {
        rotulo: 'Fuso horário',
        descricao: 'Usado para renderizar a agenda e fechar competências.',
        schema: zFuso,
        padrao: 'America/Sao_Paulo',
    },
    // 12 · planos
    regua_dias_apos_vencimento: {
        rotulo: 'Cobrança depois do vencimento (dias)',
        descricao: 'Em quantos dias depois do vencimento a régua avisa a fatura vencida. Padrão ' +
            'provisório — a cadência ainda depende da decisão do dono.',
        schema: zDiasDaReguaDepoisDoVencimento,
        padrao: [1, 7, 15],
    },
};
export const CHAVES_CONFIGURACAO = Object.keys(CONFIGURACOES);
export function ehChaveDeConfiguracao(valor) {
    return Object.prototype.hasOwnProperty.call(CONFIGURACOES, valor);
}
/**
 * O padrão de uma chave.
 *
 * Passa o padrão pelo próprio schema em vez de devolvê-lo direto, o
 * que compra duas coisas: o zod devolve um objeto NOVO — sem isso um
 * chamador que mutasse o resultado corromperia o padrão para todas as
 * arenas do processo — e um padrão declarado fora do seu próprio
 * schema estoura aqui, no primeiro uso, em vez de virar linha inválida
 * no banco.
 */
export function padraoDe(chave) {
    return CONFIGURACOES[chave].schema.parse(CONFIGURACOES[chave].padrao);
}
/** Valida (e normaliza) um valor contra o schema da sua chave. */
export function validarConfiguracao(chave, valor) {
    return CONFIGURACOES[chave].schema.parse(valor);
}
/** O mapa completo de padrões — o que uma arena nova recebe. */
export function configuracoesPadrao() {
    const mapa = {};
    for (const chave of CHAVES_CONFIGURACAO)
        mapa[chave] = padraoDe(chave);
    return mapa;
}
//# sourceMappingURL=configuracoes.js.map